import { formatMoney } from '@theme/money-formatting';
import { ThemeEvents } from '@theme/events';

/**
 * Updates free shipping bar after cart changes. Initial state is rendered server-side in Liquid.
 */
class FreeShippingBar extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    this.#abortController = new AbortController();
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, {
      signal: this.#abortController.signal,
    });
    this.#update();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  /**
   * @param {CustomEvent} event
   */
  #onCartUpdate = (event) => {
    const resource = event?.detail?.resource;
    if (resource && typeof resource.total_price === 'number') {
      this.dataset.cartTotal = String(resource.total_price);
    }
    this.#update();
  };

  #update() {
    const threshold = Number(this.dataset.threshold);
    const cartTotal = Number(this.dataset.cartTotal);

    if (!threshold || threshold <= 0) {
      this.hidden = true;
      return;
    }

    this.hidden = false;

    const progressMessage = this.dataset.progressMessage ?? '';
    const achievedMessage = this.dataset.achievedMessage ?? '';
    const currencyFormat = this.dataset.currencyFormat ?? '{{amount}}';
    const currency = this.dataset.currency ?? 'BRL';

    const messageEl = this.querySelector('[data-free-shipping-bar-message]');
    const fillEl = this.querySelector('[data-free-shipping-bar-fill]');
    const trackEl = this.querySelector('.free-shipping-bar__track');

    const remaining = threshold - cartTotal;

    if (remaining > 0) {
      const formattedAmount = formatMoney(remaining, currencyFormat, currency);

      if (messageEl) {
        messageEl.textContent = progressMessage.replace('[value]', formattedAmount);
      }

      if (fillEl instanceof HTMLElement) {
        fillEl.style.width = `${Math.min(100, (cartTotal / threshold) * 100)}%`;
      }
    } else {
      if (messageEl) {
        messageEl.textContent = achievedMessage;
      }

      if (fillEl instanceof HTMLElement) {
        fillEl.style.width = '100%';
      }
    }

    if (trackEl instanceof HTMLElement) {
      trackEl.setAttribute('aria-valuenow', String(Math.min(cartTotal, threshold)));
      if (messageEl?.textContent) {
        trackEl.setAttribute('aria-label', messageEl.textContent);
      }
    }
  }
}

if (!customElements.get('free-shipping-bar')) {
  customElements.define('free-shipping-bar', FreeShippingBar);
}
